import React from "react";
import {
  Grid,
  Box,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Autocomplete,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import COUNTRIES from "../../../constants/countries";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
  },
};

/**
 * Shared Personal Information form (as in the screenshot).
 * Used once for the primary owner, and repeated for each co-owner with optional Share %.
 *
 * @param {Object} value - name, sex, job, address, phoneNumber, email, idNumber, nationality, reservationFee, countryCode; plus percentage when showSharePercentage
 * @param {Object} errors - Field-level errors
 * @param {Function} onChange - (e) => void, e.target.name / e.target.value
 * @param {boolean} [showSharePercentage] - When true, show "Share %" field (for co-owners)
 * @param {string} [sharePercentError] - When total ownership exceeds 100%, show error on Share % field
 * @param {Function} [onRemove] - When provided, show remove button (for co-owner card)
 * @param {boolean} [compact] - When true, use size="small" and tighter layout (for co-owner)
 */
const PersonalInfoForm = ({
  value = {},
  errors = {},
  onChange,
  showSharePercentage = false,
  sharePercentError = "",
  onRemove,
  compact = false,
}) => {
  const size = compact ? "small" : "medium";

  return (
    <Box sx={compact ? { position: "relative", pr: onRemove ? 5 : 0 } : {}}>
      {onRemove && (
        <IconButton
          onClick={onRemove}
          color="error"
          size="small"
          title="Remove co-owner"
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            "&:hover": {
              bgcolor: "rgba(211, 47, 47, 0.08)",
            },
          }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      )}
      <Grid container spacing={compact ? 2 : 3}>
        <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
          <TextField
            size={size}
            variant="outlined"
            label="Full Name"
            name="name"
            value={value.name ?? ""}
            onChange={onChange}
            fullWidth
            required
            error={!!errors.name}
            helperText={errors.name}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
          <TextField
            select
            size={size}
            variant="outlined"
            label="Gender"
            name="sex"
            value={value.sex ?? ""}
            onChange={onChange}
            fullWidth
            required
            error={!!errors.sex}
            helperText={errors.sex}
            sx={textFieldSx}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
          </TextField>
        </Grid>
        <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
          <TextField
            size={size}
            variant="outlined"
            label="Job Title"
            name="job"
            value={value.job ?? ""}
            onChange={onChange}
            fullWidth
            error={!!errors.job}
            helperText={errors.job}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
          <TextField
            size={size}
            variant="outlined"
            label="Address"
            name="address"
            value={value.address ?? ""}
            onChange={onChange}
            fullWidth
            error={!!errors.address}
            helperText={errors.address}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
          <TextField
            size={size}
            variant="outlined"
            label="Phone Number"
            name="phoneNumber"
            value={value.phoneNumber ?? ""}
            onChange={onChange}
            fullWidth
            required
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TextField
                    value={value.countryCode ?? "+20"}
                    onChange={(e) =>
                      onChange({
                        target: {
                          name: "countryCode",
                          value: e.target.value,
                        },
                      })
                    }
                    variant="standard"
                    size="small"
                    inputProps={{
                      style: {
                        width: 40,
                        padding: 0,
                        fontSize: 14,
                      },
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
          <TextField
            size={size}
            variant="outlined"
            label="Email Address"
            name="email"
            value={value.email ?? ""}
            onChange={onChange}
            fullWidth
            required
            type="email"
            error={!!errors.email}
            helperText={errors.email}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
          <TextField
            size={size}
            variant="outlined"
            label="ID Number"
            name="idNumber"
            value={value.idNumber ?? ""}
            onChange={onChange}
            fullWidth
            required
            error={!!errors.idNumber}
            helperText={errors.idNumber}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
          <Autocomplete
            options={COUNTRIES}
            value={value.nationality || null}
            onChange={(_, newValue) => {
              onChange({ target: { name: "nationality", value: newValue ?? "" } });
            }}
            autoHighlight
            size={size}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Nationality"
                required
                error={!!errors.nationality}
                helperText={errors.nationality}
                sx={textFieldSx}
              />
            )}
          />
        </Grid>
        {!showSharePercentage && (
          <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
            <TextField
              size={size}
              variant="outlined"
              label="Reservation Fee"
              name="reservationFee"
              value={value.reservationFee ?? ""}
              onChange={onChange}
              fullWidth
              type="number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">EGP</InputAdornment>
                ),
              }}
              sx={textFieldSx}
            />
          </Grid>
        )}
        {showSharePercentage && (
          <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
            <TextField
              size={size}
              variant="outlined"
              label="Share %"
              name="percentage"
              value={value.percentage ?? ""}
              onChange={onChange}
              fullWidth
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.5 }}
              placeholder="e.g. 40"
              error={!!sharePercentError}
              helperText={sharePercentError || undefined}
              sx={textFieldSx}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PersonalInfoForm;
